import {
  type ReactNode,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { placePopover } from './popoverPlacement.js';

interface PopoverProps {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  children: ReactNode;
  label: string;
}

/**
 * 115: a non-modal, portal-based popover — same shape as
 * `pages/schedule/timeline/CreateBlockPopover.tsx`'s own proven pattern (portalled to
 * `document.body` to escape an `overflow-hidden` ancestor, Escape/outside-pointerdown/scroll/
 * resize dismissal, no backdrop, no `aria-modal`), generalized to anchor against a ref instead of
 * a raw click rect.
 */
export function Popover({ open, onClose, anchorRef, children, label }: PopoverProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const anchor = anchorRef.current;
    const box = boxRef.current;
    if (!anchor || !box) return;
    const anchorRect = anchor.getBoundingClientRect();
    const { width, height } = box.getBoundingClientRect();
    const placement = placePopover(
      anchorRect,
      { width, height },
      { width: window.innerWidth, height: window.innerHeight },
    );
    setStyle({ left: placement.left, top: placement.top });
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const close = () => onClose();
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      ref={boxRef}
      // biome-ignore lint/a11y/useSemanticElements: #950 role="dialog" on a positioned panel is the intended ARIA pattern; native <dialog> is hidden until opened and brings its own positioning
      role="dialog"
      aria-label={label}
      className="rounded-xl border border-hairline bg-canvas shadow-xl"
      style={{
        position: 'fixed',
        left: style?.left ?? 0,
        top: style?.top ?? 0,
        visibility: style ? 'visible' : 'hidden',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
